/**
 * AI Provider & Model configuration.
 */
export const aiProviders = {
  ollama: {
    supportsTopP: true,
    models: {
      "mistral-small3.1:24b": {},
      "mistral-small:24b": {},
      "mistral:7b": {},
      "alibayram/medgemma:27b": {},
      "alibayram/medgemma:4b": {},
    },
  },
  ollama2: {
    supportsTopP: true,
    models: {
      "mistral-small3.1:24b": {},
      "mistral-small:24b": {},
      "mistral:7b": {},
      "alibayram/medgemma:27b": {},
      "alibayram/medgemma:4b": {},
    },
  },
  berget: {
    supportsTopP: true,
    models: {
      "mistralai/Mistral-Small-3.2-24B-Instruct-2506": {},
      "google/gemma-4-31B-it": {thinkingLevels: ["none", "high"]},
    },
  },
  vllm: {
    supportsTopP: true,
    models: {
      "google/gemma-4-31B-it": {},
      "nvidia/Gemma-4-31B-IT-NVFP4": {},
    },
  },
  mistral: {
    supportsTopP: true,
    models: {
      "mistral-large-2512": {},
      "mistral-large-2407": {},
      "mistral-medium-2508": {},
      "mistral-small-2603": {thinkingLevels: ["none", "high"]},
      "mistral-small-2506": {},
      "magistral-medium-2509": {},
      "magistral-small-2509": {},
      "ministral-14b-2512": {},
      "ministral-8b-2512": {},
      "ministral-3b-2512": {},
    },
  },
  vertex: {
    supportsTopP: true,
    models: {
      "gemini-3.1-pro-preview": {thinkingLevels: ["LOW", "MEDIUM", "HIGH"], supportsThinkingBudget: true},
      "gemini-3-flash-preview": {thinkingLevels: ["MINIMAL", "LOW", "MEDIUM", "HIGH"], supportsThinkingBudget: true},
      "gemini-3-pro-preview": {thinkingLevels: ["LOW", "HIGH"], supportsThinkingBudget: true},
      "gemini-2.5-flash-lite": {supportsThinkingBudget: true},
      "gemini-2.5-pro": {supportsThinkingBudget: true},
      "gemini-2.5-flash": {supportsThinkingBudget: true},
    },
  },
  gemini: {
    supportsTopP: true,
    models: {
      "gemma-4-26b-a4b-it": {thinkingLevels: ["minimal", "high"]},
      "gemma-4-31b-it": {thinkingLevels: ["minimal", "high"]},
      "gemini-3.1-pro-preview": {thinkingLevels: ["LOW", "MEDIUM", "HIGH"], supportsThinkingBudget: true},
      "gemini-3.1-flash-lite-preview": {
        thinkingLevels: ["MINIMAL", "LOW", "MEDIUM", "HIGH"],
        supportsThinkingBudget: true,
      },
      "gemini-3-pro-preview": {thinkingLevels: ["LOW", "HIGH"], supportsThinkingBudget: true},
      "gemini-3-flash-preview": {thinkingLevels: ["MINIMAL", "LOW", "MEDIUM", "HIGH"], supportsThinkingBudget: true},
      "gemini-2.5-pro": {supportsThinkingBudget: true},
      "gemini-2.5-flash": {supportsThinkingBudget: true},
      "gemini-2.5-flash-lite": {supportsThinkingBudget: true},
    },
  },
  openai: {
    supportsTopP: true,
    models: {
      "gpt-5.2": {thinkingLevels: ["none", "low", "medium", "high", "xhigh"], supportsTemperature: false},
      "gpt-5.1": {thinkingLevels: ["none", "low", "medium", "high"], supportsTemperature: false},
      "gpt-5": {thinkingLevels: ["minimal", "low", "medium", "high"], supportsTemperature: false},
      "gpt-5-mini": {thinkingLevels: ["minimal", "low", "medium", "high"], supportsTemperature: false},
      "gpt-5-nano": {thinkingLevels: ["minimal", "low", "medium", "high"], supportsTemperature: false},
      "gpt-4.1": {},
    },
  },
  anthropic: {
    supportsTopP: true,
    models: {
      "claude-opus-4-6": {thinkingLevels: ["OFF", "ON"], supportsThinkingBudget: true},
      "claude-sonnet-4-6": {thinkingLevels: ["OFF", "ON"], supportsThinkingBudget: true},
      "claude-haiku-4-5": {thinkingLevels: ["OFF", "ON"], supportsThinkingBudget: true},
    },
  },
};

/**
 * Returns resolved capability settings for a given provider + model combination.
 *
 * @param {string} provider
 * @param {string} model
 * @returns {{supportsThinking: boolean, supportsThinkingBudget: boolean, supportsTopP: boolean, thinkingLevels: string[]}}
 */
export function getModelSettings(provider, model) {
  const providerConfig = aiProviders[provider];

  if (!providerConfig) {
    return {supportsThinking: false, supportsThinkingBudget: false, supportsTopP: false, thinkingLevels: []};
  }

  const modelConfig = providerConfig.models?.[model] ?? {};
  const thinkingLevels = modelConfig.thinkingLevels ?? [];

  return {
    supportsTemperature: modelConfig.supportsTemperature ?? true,
    supportsThinking: thinkingLevels.length > 0,
    supportsThinkingBudget: modelConfig.supportsThinkingBudget ?? false,
    supportsTopP: providerConfig.supportsTopP ?? false,
    thinkingLevels,
  };
}
